import { useEffect, useState, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { api } from '../services/api'
import { Material, Quiz, Mindmap, Summary, Question, QuizVersionMeta, ChatMessage } from '../types'
import QuizCard from '../components/QuizCard'
import MindmapViewer from '../components/MindmapViewer'
import ChatPanel from '../components/ChatPanel'
import LoadingSpinner from '../components/LoadingSpinner'
import { FileText, Brain, Map, ArrowLeft, ChevronLeft, ChevronRight, RefreshCw, Trophy, RotateCcw, Clock, Copy, Download, BookOpen, Zap, Sparkles, MessageCircle } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import { useWebSocket } from '../hooks/useWebSocket'
import { useAuthStore } from '../stores/authStore'
import { useUpgradeModal } from '../stores/upgradeModalStore'

type StudyMode = 'select' | 'summary' | 'quiz' | 'mindmap' | 'chat'
type Difficulty = 'easy' | 'medium' | 'hard'
type DetailLevel = 'executive' | 'standard' | 'comprehensive'

const DIFFICULTY_OPTIONS: { value: Difficulty; label: string; color: string }[] = [
  { value: 'easy',   label: 'Fácil',   color: '#10B981' },
  { value: 'medium', label: 'Medio',   color: '#FBBF24' },
  { value: 'hard',   label: 'Difícil', color: '#EF4444' },
]

const DETAIL_OPTIONS: { value: DetailLevel; label: string; desc: string }[] = [
  { value: 'standard',      label: 'Estándar',     desc: '~500 palabras' },
  { value: 'comprehensive', label: 'Exhaustivo',   desc: '~1000 palabras' },
]

export default function StudyPage() {
  const { materialId } = useParams<{ materialId: string }>()
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const { open: openUpgradeModal } = useUpgradeModal()
  const isPro = user?.plan === 'pro'

  const [material, setMaterial] = useState<Material | null>(null)
  const [mode, setMode] = useState<StudyMode>('select')
  const [isLoading, setIsLoading] = useState(true)
  const [isGenerating, setIsGenerating] = useState(false)
  const [isExportingPdf, setIsExportingPdf] = useState(false)

  // Study content
  const [summary, setSummary] = useState<Summary | null>(null)
  const [quiz, setQuiz] = useState<Quiz | null>(null)
  const [mindmap, setMindmap] = useState<Mindmap | null>(null)

  // Quiz one-by-one state
  const [currentQuestion, setCurrentQuestion] = useState(0)
  const [answers, setAnswers] = useState<Record<string, string>>({})
  const [answeredCurrent, setAnsweredCurrent] = useState(false)
  const [showFinalResults, setShowFinalResults] = useState(false)
  const [score, setScore] = useState(0)
  const [startTime] = useState(() => Date.now())

  // Controls
  const [difficulty, setDifficulty] = useState<Difficulty>('medium')
  const [questionCount, setQuestionCount] = useState<number>(15)
  const [detailLevel, setDetailLevel] = useState<DetailLevel>('standard')

  // Quiz versioning
  const [quizVersions, setQuizVersions] = useState<QuizVersionMeta[]>([])
  const [isLoadingVersion, setIsLoadingVersion] = useState(false)
  // Cache: stores already-generated summary content per detail level to avoid redundant regeneration
  const [summaryCache, setSummaryCache] = useState<Partial<Record<DetailLevel, string>>>({})
  const [summaryKeyConceptsCache, setSummaryKeyConceptsCache] = useState<Partial<Record<DetailLevel, string[]>>>({})

  // Chat (Sprint 1-C)
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([])
  const [chatStreamingId, setChatStreamingId] = useState<string | null>(null)
  const [chatStreamingText, setChatStreamingText] = useState('')
  const [chatSending, setChatSending] = useState(false)
  const [chatError, setChatError] = useState<string | null>(null)

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const parseQuizContent = (content: any): Question[] =>
    (content?.multiple_choice || []).map((q: any, index: number) => ({
      id: q.question_id || `q${index}`,
      question: q.question_text || '',
      options: q.options || [],
      correctAnswer: q.options?.[q.correct_option_index] ?? '',
      explanation: q.explanation || '',
      difficulty: (q.difficulty as Difficulty) || 'medium',
      sourceReference: q.source_reference,
    }))

  // WebSocket connection
  useWebSocket({
    onMessage: (message) => {
      if (message.material_id && message.material_id !== materialId) return

      switch (message.type) {
        case 'summary_ready': {
          const receivedLevel = (message.detail_level as DetailLevel | undefined) ?? 'standard'
          const levelField = `summary_content_${receivedLevel}` as 'summary_content_standard' | 'summary_content_comprehensive'
          setMaterial((m) => m ? {
            ...m,
            summary_status: 'ready',
            summary_content: message.content,
            [levelField]: message.content,
            summary_key_concepts: message.key_concepts ?? m.summary_key_concepts,
          } : m)
          if (message.content) {
            setSummary({
              materialId: materialId!,
              content: message.content,
              keyPoints: message.key_concepts ?? [],
              createdAt: new Date().toISOString(),
            })
            setDetailLevel(receivedLevel)
            setSummaryCache((c) => ({ ...c, [receivedLevel]: message.content }))
            if (message.key_concepts) {
              setSummaryKeyConceptsCache((c) => ({ ...c, [receivedLevel]: message.key_concepts! }))
            }
            setMode('summary')
          }
          setIsGenerating(false)
          break
        }
        case 'quiz_ready': {
          setMaterial((m) => m ? { ...m, quiz_status: 'ready', quiz_content: message.content, quiz_difficulty: message.difficulty } : m)
          const newQuestions = parseQuizContent(message.content)
          if (newQuestions.length > 0) {
            // Use the difficulty sent by the Lambda — per-question difficulty is not in the LLM schema
            const arrivedDifficulty = message.difficulty ?? difficulty
            setDifficulty(arrivedDifficulty)
            setQuiz((prev) => prev
              ? { ...prev, questions: newQuestions, difficulty: arrivedDifficulty, versionId: undefined, regenerationFailed: false }
              : { quizId: materialId!, materialId: materialId!, questions: newQuestions, difficulty: arrivedDifficulty, createdAt: new Date().toISOString() }
            )
            setAnswers({})
            setCurrentQuestion(0)
            setAnsweredCurrent(false)
            setShowFinalResults(false)
            setMode('quiz')
            // Refresh version list after new quiz is saved
            if (materialId) {
              api.getQuizVersions(materialId)
                .then((r) => setQuizVersions(r.versions))
                .catch(() => {})
            }
          }
          setIsGenerating(false)
          break
        }
        case 'mindmap_ready':
          setMaterial((m) => m ? { ...m, mindmap_status: 'ready', mindmap_content: message.content } : m)
          setIsGenerating(false)
          break
        case 'summary_failed':
        case 'mindmap_failed': {
          const ct = message.type.replace('_failed', '')
          setMaterial((m) => m ? { ...m, [`${ct}_status`]: 'failed' } : m)
          setIsGenerating(false)
          break
        }
        case 'quiz_failed': {
          // Mark as failed but preserve existing quiz_content so the old quiz remains accessible
          setMaterial((m) => m ? { ...m, quiz_status: 'failed' } : m)
          setIsGenerating(false)
          // If we're in quiz mode already, stay there; just flag the failed regeneration
          setQuiz((prev) => prev ? { ...prev, regenerationFailed: true } : prev)
          break
        }
        case 'flashcards_ready':
          setMaterial((m) => m ? {
            ...m,
            flashcards_status: 'ready',
            flashcards_count: message.count,
          } : m)
          break
        case 'flashcards_failed':
          setMaterial((m) => m ? {
            ...m,
            flashcards_status: 'failed',
            flashcards_error: message.error,
          } : m)
          break
        // ===== Chat RAG (Sprint 1-C) =====
        case 'chat_ready':
          setMaterial((m) => m ? { ...m, chat_status: 'ready' } : m)
          break
        case 'chat_failed':
          if (message.messageId) {
            // per-message failure
            setChatError(message.error || 'No se pudo generar la respuesta.')
            setChatStreamingId(null)
            setChatStreamingText('')
            setChatSending(false)
          } else {
            // material-level ingestion failure
            setMaterial((m) => m ? { ...m, chat_status: 'failed' } : m)
          }
          break
        case 'chat_start':
          setChatStreamingId(message.messageId ?? null)
          setChatStreamingText('')
          setChatError(null)
          break
        case 'chat_chunk':
          if (message.messageId === chatStreamingId || chatStreamingId === null) {
            setChatStreamingText((t) => t + (message.delta ?? ''))
          }
          break
        case 'chat_complete': {
          const assistantMsg: ChatMessage = {
            materialId: materialId!,
            messageId: message.messageId ?? `srv-${Date.now()}`,
            userId: user?.id ?? '',
            role: 'assistant',
            content: message.content ?? '',
            createdAt: new Date().toISOString(),
            citations: message.citations,
          }
          setChatMessages((prev) => [...prev, assistantMsg])
          setChatStreamingId(null)
          setChatStreamingText('')
          setChatSending(false)
          break
        }
      }
    },
    onConnect: () => {},
    onDisconnect: () => {},
  })

  useEffect(() => {
    loadMaterial()
  }, [materialId])

  const loadMaterial = async () => {
    if (!materialId) return
    try {
      const data = await api.getMaterial(materialId) as Material
      setMaterial(data)
    } catch {
      // ignore
    } finally {
      setIsLoading(false)
    }
  }

  // ---------- chat ----------

  const openChat = async () => {
    if (!material || !materialId) return
    if (material.chat_status !== 'ready') return
    setMode('chat')
    if (chatMessages.length === 0) {
      try {
        const r = await api.getChatHistory(materialId)
        setChatMessages(r.messages as ChatMessage[])
      } catch {
        // ignore
      }
    }
  }

  const sendChat = async (content: string) => {
    if (!materialId) return
    setChatError(null)
    setChatSending(true)
    const userMsg: ChatMessage = {
      materialId,
      messageId: `local-${Date.now()}`,
      userId: user?.id ?? '',
      role: 'user',
      content,
      createdAt: new Date().toISOString(),
    }
    setChatMessages((prev) => [...prev, userMsg])
    try {
      const r = await api.sendChatMessage(materialId, content)
      setChatStreamingId(r.assistantMessageId)
    } catch (e) {
      setChatSending(false)
      const err = e as { status?: number; message?: string; body?: { detail?: { error?: string; message?: string } } }
      if (err?.status === 403 && err?.body?.detail?.error === 'plan_required') {
        openUpgradeModal('unlimited_chat')
      } else {
        setChatError(err?.body?.detail?.message || err?.message || 'Error al enviar.')
      }
    }
  }

  const clearChat = async () => {
    if (!materialId) return
    try {
      await api.deleteChatHistory(materialId)
      setChatMessages([])
    } catch {
      // ignore
    }
  }

  // ---------- loaders ----------

  const loadSummary = () => {
    if (!material) return
    if (material.summary_status === 'ready') {
      // Prefer the standard-level field; fall back to the generic summary_content
      const standardContent = material.summary_content_standard || material.summary_content
      const comprehensiveContent = material.summary_content_comprehensive
      if (standardContent) {
        const content = typeof standardContent === 'string' ? standardContent : JSON.stringify(standardContent)
        // Seed both caches with whatever the backend already has
        setSummaryCache((c) => ({
          ...c,
          standard: content,
          ...(comprehensiveContent ? { comprehensive: comprehensiveContent } : {}),
        }))
        if (material.summary_key_concepts?.length) {
          setSummaryKeyConceptsCache((c) => ({ ...c, standard: material.summary_key_concepts! }))
        }
        setDetailLevel('standard')
        setSummary({
          materialId: materialId!,
          content,
          keyPoints: [],
          createdAt: material.summary_generated_at || new Date().toISOString(),
        })
        setMode('summary')
      } else {
        alert('El resumen aún no está disponible.')
      }
    } else if (material.summary_status === 'processing') {
      setIsGenerating(true)
      setMode('summary')
    } else {
      alert('El resumen aún no está disponible.')
    }
  }

  const loadQuiz = useCallback((showFailedWarning = false) => {
    if (!material) return
    const hasContent = material.quiz_content && (material.quiz_content.multiple_choice || []).length > 0
    if ((material.quiz_status === 'ready' || (material.quiz_status === 'failed' && hasContent)) && hasContent) {
      const questions: Question[] = (material.quiz_content!.multiple_choice || []).map(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (q: any, index: number) => ({
          id: q.question_id || `q${index}`,
          question: q.question_text || '',
          options: q.options || [],
          correctAnswer: q.options?.[q.correct_option_index] ?? '',
          explanation: q.explanation || '',
          difficulty: (q.difficulty as Difficulty) || 'medium',
          sourceReference: q.source_reference,
        })
      )
      const loadedDifficulty = (material.quiz_difficulty as Difficulty) || 'medium'
      setDifficulty(loadedDifficulty)
      setQuiz({
        quizId: material.materialId,
        materialId: materialId!,
        questions,
        difficulty: loadedDifficulty,
        createdAt: material.quiz_generated_at || new Date().toISOString(),
        versionId: undefined,
        regenerationFailed: showFailedWarning || material.quiz_status === 'failed',
      })
      setAnswers({})
      setCurrentQuestion(0)
      setAnsweredCurrent(false)
      setShowFinalResults(false)
      setMode('quiz')
      // Fetch version list in background
      if (materialId) {
        api.getQuizVersions(materialId)
          .then((r) => setQuizVersions(r.versions))
          .catch(() => {/* versions are non-critical */})
      }
    } else if (material.quiz_status === 'processing') {
      setIsGenerating(true)
    } else {
      alert('El quiz aún no está disponible.')
    }
  }, [material, materialId])

  const handleLoadVersion = async (version: QuizVersionMeta) => {
    if (!materialId || isLoadingVersion) return
    // If clicking the currently active version, do nothing
    if (quiz?.versionId === version.version_id) return
    setIsLoadingVersion(true)
    try {
      const v = await api.getQuizVersion(materialId, version.version_id)
      const questions: Question[] = ((v.content?.multiple_choice) || []).map(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (q: any, index: number) => ({
          id: q.question_id || `q${index}`,
          question: q.question_text || '',
          options: q.options || [],
          correctAnswer: q.options?.[q.correct_option_index] ?? '',
          explanation: q.explanation || '',
          difficulty: (q.difficulty as Difficulty) || 'medium',
          sourceReference: q.source_reference,
        })
      )
      setQuiz({
        quizId: materialId,
        materialId,
        questions,
        difficulty: v.difficulty,
        createdAt: v.created_at,
        versionId: v.version_id,
        regenerationFailed: false,
      })
      setAnswers({})
      setCurrentQuestion(0)
      setAnsweredCurrent(false)
      setShowFinalResults(false)
    } catch {
      alert('Error al cargar la versión del quiz.')
    } finally {
      setIsLoadingVersion(false)
    }
  }

  const loadMindmap = () => {
    if (!material) return
    if (material.mindmap_status === 'ready' && material.mindmap_content) {
      setMindmap(material.mindmap_content)
      setMode('mindmap')
    } else if (material.mindmap_status === 'processing') {
      setIsGenerating(true)
    } else {
      alert('El mapa mental aún no está disponible.')
    }
  }

  const handleRegenerateSummary = async (level: 'standard' | 'comprehensive') => {
    if (level === 'comprehensive' && !isPro) { openUpgradeModal('detail_levels'); return }
    if (!materialId) return
    setIsGenerating(true)
    try {
      await api.regenerateSummary(materialId, level)
      // WebSocket summary_ready will update summary state
    } catch {
      setIsGenerating(false)
      alert('Error al regenerar el resumen.')
    }
  }

  const handleRegenerateQuiz = async () => {
    if (!isPro) { openUpgradeModal('regenerate_quiz'); return }
    if (!materialId) return
    setIsGenerating(true)
    try {
      await api.regenerateQuiz(materialId, questionCount, difficulty)
      // WebSocket quiz_ready will handle updating the quiz and resetting state
    } catch {
      setIsGenerating(false)
      // Restore the previous quiz so the user isn't left with nothing
      loadQuiz(true)
    }
  }

  // ---------- quiz flow ----------

  const handleAnswerSelect = (questionId: string, answer: string) => {
    setAnswers((prev) => ({ ...prev, [questionId]: answer }))
    setAnsweredCurrent(true)
  }

  const handleNextQuestion = () => {
    if (!quiz) return
    if (currentQuestion < quiz.questions.length - 1) {
      setCurrentQuestion((i) => i + 1)
      setAnsweredCurrent(false)
    } else {
      finishQuiz()
    }
  }

  const handlePrevQuestion = () => {
    if (currentQuestion <= 0 || !quiz) return
    const prevIdx = currentQuestion - 1
    setCurrentQuestion(prevIdx)
    setAnsweredCurrent(false)
  }

  const finishQuiz = async () => {
    if (!quiz || !materialId) return

    const answersArray = quiz.questions.map((q) => ({
      question_id: q.id,
      user_answer: answers[q.id] || '',
      correct: answers[q.id] === q.correctAnswer,
      time_taken: null,
    }))

    const correct = answersArray.filter((a) => a.correct).length
    const finalScore = Math.round((correct / quiz.questions.length) * 100)
    setScore(finalScore)
    setShowFinalResults(true)

    const timeSpent = Math.round((Date.now() - startTime) / 1000)
    try {
      await api.submitQuizResults({
        session_id: quiz.quizId,
        material_id: materialId,
        answers: answersArray,
        time_spent: timeSpent,
        difficulty: quiz.difficulty as 'easy' | 'medium' | 'hard',
        ...(quiz.versionId ? { quiz_version_id: quiz.versionId } : {}),
      })
    } catch {
      // Non-critical
    }
  }

  const resetQuiz = () => {
    setAnswers({})
    setCurrentQuestion(0)
    setAnsweredCurrent(false)
    setShowFinalResults(false)
    setScore(0)
  }

  // ---------- render helpers ----------

  const statusBadge = (status?: string) => {
    if (status === 'ready') return { label: 'Listo', color: '#10B981' }
    if (status === 'processing') return { label: 'Generando...', color: '#FBBF24' }
    if (status === 'failed') return { label: 'Error', color: '#EF4444' }
    return { label: 'Pendiente', color: '#475569' }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" text="Cargando material..." />
      </div>
    )
  }

  if (!material) {
    return (
      <div className="text-center py-12">
        <p style={{ color: '#94A3B8' }}>Material no encontrado</p>
        <button onClick={() => navigate('/materials')} className="btn-secondary mt-4">
          Volver a materiales
        </button>
      </div>
    )
  }

  const q = quiz?.questions[currentQuestion]

  return (
    <div className={`${mode === 'summary' || mode === 'mindmap' ? 'max-w-5xl' : mode === 'chat' ? 'max-w-4xl' : 'max-w-3xl'} space-y-6`}>
      {/* Back */}
      <button
        onClick={() => mode === 'select' ? navigate('/materials') : setMode('select')}
        className="flex items-center gap-2 text-sm transition-colors"
        style={{ color: '#64748B' }}
        onMouseEnter={(e) => { e.currentTarget.style.color = '#E2E8F0' }}
        onMouseLeave={(e) => { e.currentTarget.style.color = '#64748B' }}
      >
        <ArrowLeft className="w-4 h-4" />
        {mode === 'select' ? 'Volver a materiales' : 'Volver a opciones'}
      </button>

      {/* Title */}
      <div>
        <h1 className="text-2xl font-bold" style={{ color: '#F1F5F9' }}>{material.title}</h1>
        {material.metadata?.pages && (
          <p className="text-sm mt-1" style={{ color: '#64748B' }}>
            {material.metadata.pages} página{material.metadata.pages !== 1 ? 's' : ''}
          </p>
        )}
      </div>

      {/* ======= SELECT MODE ======= */}
      {mode === 'select' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-5">
          {(
            [
              {
                key: 'summary',
                contentType: 'summary',
                label: 'Resumen',
                icon: FileText,
                color: '#818CF8',
                status: material.summary_status,
                onOpen: loadSummary,
              },
              {
                key: 'quiz',
                contentType: 'quiz',
                label: 'Quiz',
                icon: Brain,
                color: '#10B981',
                status: material.quiz_status,
                onOpen: () => loadQuiz(),
              },
              {
                key: 'mindmap',
                contentType: 'mindmap',
                label: 'Mapa Mental',
                icon: Map,
                color: '#FBBF24',
                status: material.mindmap_status,
                onOpen: loadMindmap,
              },
            ] as const
          ).map(({ key, contentType, label, icon: Icon, color, status, onOpen }) => {
            const ready = status === 'ready'
            const processing = status === 'processing'
            const failed = status === 'failed'
            const badge = statusBadge(status)
            const handleClick = async () => {
              if (ready) { onOpen(); return }
              if (processing) return
              if (failed && materialId) {
                // Optimistic UI: mark as processing immediately so the user sees feedback.
                // The Lambda runs async; the WebSocket pushes quiz_ready / quiz_failed when done.
                const statusField = `${contentType}_status` as const
                setMaterial((m) => m ? { ...m, [statusField]: 'processing' } : m)
                try {
                  await api.retryGeneration(materialId, contentType)
                } catch (e) {
                  setMaterial((m) => m ? { ...m, [statusField]: 'failed' } : m)
                  alert(`No se pudo reintentar la generación: ${(e as Error).message}`)
                }
              }
            }
            const interactive = ready || failed
            return (
              <button
                key={key}
                onClick={handleClick}
                disabled={processing}
                className="glass-card p-6 text-left transition-all disabled:cursor-not-allowed"
                style={interactive ? { border: `1px solid ${color}30` } : { opacity: processing ? 0.7 : 0.5 }}
                onMouseEnter={(e) => { if (interactive) e.currentTarget.style.borderColor = `${color}60` }}
                onMouseLeave={(e) => { if (interactive) e.currentTarget.style.borderColor = `${color}30` }}
              >
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                  style={{ background: `${color}15` }}
                >
                  <Icon className="w-6 h-6" style={{ color }} />
                </div>
                <h3 className="font-semibold mb-1" style={{ color: '#F1F5F9' }}>{label}</h3>
                <div className="flex items-center gap-1.5">
                  <div className="w-1.5 h-1.5 rounded-full" style={{ background: badge.color }} />
                  <span className="text-xs" style={{ color: badge.color }}>
                    {failed ? 'Error — reintentar' : badge.label}
                  </span>
                </div>
                {(ready || failed) && (
                  <div className="flex items-center gap-1 mt-3">
                    <span className="text-xs" style={{ color: failed ? '#EF4444' : color }}>
                      {failed ? 'Reintentar' : 'Abrir'}
                    </span>
                    <ChevronRight className="w-3 h-3" style={{ color: failed ? '#EF4444' : color }} />
                  </div>
                )}
              </button>
            )
          })}

          {/* Chat card (Sprint 1-C) */}
          {(() => {
            const color = '#38BDF8'
            const cs = material.chat_status
            const ready = cs === 'ready'
            const indexing = cs === 'indexing'
            const failed = cs === 'failed'
            return (
              <button
                key="chat"
                onClick={openChat}
                disabled={!ready}
                className="glass-card p-6 text-left transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                style={ready ? { border: `1px solid ${color}30` } : {}}
                onMouseEnter={(e) => { if (ready) e.currentTarget.style.borderColor = `${color}60` }}
                onMouseLeave={(e) => { if (ready) e.currentTarget.style.borderColor = `${color}30` }}
              >
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                  style={{ background: `${color}15` }}
                >
                  <MessageCircle className="w-6 h-6" style={{ color }} />
                </div>
                <h3 className="font-semibold mb-1" style={{ color: '#F1F5F9' }}>Chat</h3>
                <div className="flex items-center gap-1.5">
                  <div
                    className="w-1.5 h-1.5 rounded-full"
                    style={{ background: ready ? '#10B981' : indexing ? '#FBBF24' : failed ? '#EF4444' : '#64748B' }}
                  />
                  <span className="text-xs" style={{ color: ready ? '#10B981' : indexing ? '#FBBF24' : failed ? '#EF4444' : '#94A3B8' }}>
                    {ready ? 'Listo' : indexing ? 'Indexando…' : failed ? 'Error' : 'No disponible'}
                  </span>
                </div>
                {ready && (
                  <div className="flex items-center gap-1 mt-3">
                    <span className="text-xs" style={{ color }}>Preguntar</span>
                    <ChevronRight className="w-3 h-3" style={{ color }} />
                  </div>
                )}
              </button>
            )
          })()}

          {/* Flashcards card (Sprint 1-B) */}
          {(() => {
            const color = '#A78BFA'
            const fcStatus = material.flashcards_status
            const ready = fcStatus === 'ready'
            const processing = fcStatus === 'processing'
            const badge = statusBadge(fcStatus)
            const handleClick = async () => {
              if (ready) { navigate('/review'); return }
              if (processing) return
              if (!materialId) return
              try {
                setMaterial((m) => m ? { ...m, flashcards_status: 'processing' } : m)
                await api.generateFlashcards(materialId, 15)
              } catch (e) {
                setMaterial((m) => m ? {
                  ...m,
                  flashcards_status: 'failed',
                  flashcards_error: (e as Error).message,
                } : m)
              }
            }
            return (
              <button
                key="flashcards"
                onClick={handleClick}
                disabled={processing}
                className="glass-card p-6 text-left transition-all disabled:cursor-not-allowed"
                style={{ border: `1px solid ${color}30` }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = `${color}60` }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = `${color}30` }}
              >
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                  style={{ background: `${color}15` }}
                >
                  <Sparkles className="w-6 h-6" style={{ color }} />
                </div>
                <h3 className="font-semibold mb-1" style={{ color: '#F1F5F9' }}>Flashcards</h3>
                <div className="flex items-center gap-1.5">
                  <div className="w-1.5 h-1.5 rounded-full" style={{ background: badge.color }} />
                  <span className="text-xs" style={{ color: badge.color }}>
                    {ready
                      ? `${material.flashcards_count ?? ''} listas`
                      : processing
                        ? 'Generando…'
                        : fcStatus === 'failed'
                          ? 'Error — reintentar'
                          : 'Generar'}
                  </span>
                </div>
                <div className="flex items-center gap-1 mt-3">
                  <span className="text-xs" style={{ color }}>
                    {ready ? 'Repasar' : processing ? 'Espera…' : 'Crear con IA'}
                  </span>
                  {!processing && <ChevronRight className="w-3 h-3" style={{ color }} />}
                </div>
              </button>
            )
          })()}
        </div>
      )}

      {/* ======= GENERATING ======= */}
      {isGenerating && (
        <div className="glass-card p-12 flex flex-col items-center gap-4">
          <LoadingSpinner size="lg" text="Generando contenido de estudio..." />
        </div>
      )}

      {/* ======= SUMMARY MODE ======= */}
      {mode === 'summary' && summary && !isGenerating && (() => {
        const wordCount = summary.content.match(/\S+/g)?.length ?? 0
        const readMin = Math.max(1, Math.ceil(wordCount / 200))
        const pages = material?.metadata?.pages
        // Prefer level-specific cached concepts → backend concepts → regex fallback
        const keyConcepts =
          summaryKeyConceptsCache[detailLevel]?.length ? summaryKeyConceptsCache[detailLevel]! :
          material?.summary_key_concepts?.length ? material.summary_key_concepts :
          (summary.content.match(/^#{1,3} (.+)/gm) ?? [])
            .map(h => h.replace(/^#+\s/, '').replace(/\*\*/g, ''))
            .filter(Boolean)
            .slice(0, 8)
        const CONCEPT_COLORS = ['#4F46E5', '#8B5CF6', '#10B981', '#F59E0B', '#F43F5E', '#06B6D4', '#EC4899', '#84CC16']

        const handleCopy = () => {
          navigator.clipboard.writeText(summary.content)
        }

        return (
          <div className="space-y-4">
            {/* Controls bar */}
            <div className="glass-card px-4 py-3 flex items-center gap-4 flex-wrap no-print">
              {/* Detail level pills */}
              <div className="flex items-center gap-1.5 flex-1">
                {DETAIL_OPTIONS.map((opt) => {
                  const active = detailLevel === opt.value
                  const disabled = opt.value === 'comprehensive' && !isPro
                  return (
                    <button
                      key={opt.value}
                      onClick={() => {
                        if (disabled) { openUpgradeModal('detail_levels'); return }
                        const level = opt.value as 'standard' | 'comprehensive'
                        setDetailLevel(level)
                        // 1st: check in-memory cache (fastest)
                        if (summaryCache[level]) {
                          setSummary((prev) => prev ? {
                            ...prev,
                            content: summaryCache[level]!,
                            keyPoints: summaryKeyConceptsCache[level] ?? prev.keyPoints,
                          } : prev)
                          return
                        }
                        // 2nd: check the level-specific field already stored in the material (DynamoDB)
                        const storedContent = level === 'standard'
                          ? (material?.summary_content_standard || material?.summary_content)
                          : material?.summary_content_comprehensive
                        if (storedContent) {
                          setSummaryCache((c) => ({ ...c, [level]: storedContent }))
                          setSummary((prev) => prev ? { ...prev, content: storedContent } : prev)
                          return
                        }
                        // 3rd: nothing stored — generate
                        handleRegenerateSummary(level)
                      }}
                      className="px-3 py-1.5 rounded-lg text-xs font-semibold transition-all"
                      style={{
                        background: active ? 'rgba(79,70,229,0.2)' : 'rgba(255,255,255,0.04)',
                        border: `1px solid ${active ? '#4F46E5' : 'rgba(255,255,255,0.06)'}`,
                        color: disabled ? '#334155' : active ? '#818CF8' : '#64748B',
                      }}
                    >
                      {opt.label}
                      <span className="ml-1 opacity-60">{opt.desc}</span>
                    </button>
                  )
                })}
              </div>
              {/* Actions */}
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={handleCopy}
                  className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg transition-all"
                  style={{ color: '#94A3B8', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }}
                  onMouseEnter={e => { e.currentTarget.style.color = '#F1F5F9'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.15)' }}
                  onMouseLeave={e => { e.currentTarget.style.color = '#94A3B8'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)' }}
                >
                  <Copy className="w-3.5 h-3.5" /> Copiar
                </button>
                <button
                  onClick={async () => {
                    if (!materialId || isExportingPdf) return
                    setIsExportingPdf(true)
                    try {
                      const blob = await api.exportSummaryPdf(materialId, detailLevel as 'standard' | 'comprehensive')
                      const url = URL.createObjectURL(blob)
                      const a = document.createElement('a')
                      a.href = url
                      a.download = `kapri-resumen-${detailLevel}.pdf`
                      a.click()
                      URL.revokeObjectURL(url)
                    } catch {
                      alert('Error al generar el PDF. Intenta de nuevo.')
                    } finally {
                      setIsExportingPdf(false)
                    }
                  }}
                  disabled={isExportingPdf}
                  className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg transition-all disabled:opacity-60"
                  style={{ color: '#818CF8', background: 'rgba(79,70,229,0.08)', border: '1px solid rgba(79,70,229,0.2)' }}
                  onMouseEnter={e => { if (!isExportingPdf) e.currentTarget.style.background = 'rgba(79,70,229,0.18)' }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'rgba(79,70,229,0.08)' }}
                >
                  <Download className="w-3.5 h-3.5" />
                  {isExportingPdf ? 'Generando...' : 'Descargar PDF'}
                </button>
              </div>
            </div>

            {/* Stats chips */}
            <div className="flex items-center gap-3 no-print">
              {[
                { icon: FileText, label: pages ? `${pages} páginas` : null },
                { icon: BookOpen, label: `~${wordCount.toLocaleString()} palabras` },
                { icon: Clock, label: `~${readMin} min lectura` },
              ].filter(c => c.label).map(({ icon: Icon, label }) => (
                <div
                  key={label}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium"
                  style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)', color: '#64748B' }}
                >
                  <Icon className="w-3 h-3" />
                  {label}
                </div>
              ))}
            </div>

            {/* Content + sidebar */}
            <div className={keyConcepts.length > 0 ? 'grid gap-6' : ''} style={{ gridTemplateColumns: keyConcepts.length > 0 ? '1fr 240px' : undefined }}>
              {/* Main content card */}
              <div className="glass-card p-7 print-content">
                {/* Header */}
                <div className="mb-5">
                  <p className="text-xs font-bold uppercase tracking-widest mb-2" style={{ color: 'rgba(129,140,248,0.7)' }}>
                    Resumen generado por IA
                  </p>
                  <h2 className="text-xl font-bold mb-2 leading-snug" style={{ color: '#F1F5F9' }}>
                    {material?.title ?? 'Resumen'}
                  </h2>
                  <div className="flex items-center gap-2 flex-wrap">
                    {material?.metadata?.language && (
                      <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ background: 'rgba(79,70,229,0.15)', color: '#818CF8' }}>
                        {material.metadata.language}
                      </span>
                    )}
                    {summary.createdAt && (
                      <span className="text-xs" style={{ color: '#475569' }}>
                        Generado {new Date(summary.createdAt).toLocaleDateString('es-MX', { day: 'numeric', month: 'short' })}
                      </span>
                    )}
                  </div>
                </div>

                <div className="mb-6" style={{ height: 1, background: 'rgba(255,255,255,0.06)' }} />

                {/* Body with larger text */}
                <div
                  className="prose prose-invert max-w-none summary-body"
                  style={{ color: '#CBD5E1' }}
                >
                  <ReactMarkdown>{summary.content}</ReactMarkdown>
                </div>

                {/* Print-only footer — hidden in normal view */}
                <div className="print-footer" style={{ display: 'none' }}>
                  <div style={{ borderTop: '1px solid #e2e8f0', marginTop: 40, paddingTop: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 11, color: '#94a3b8' }}>
                    <span>Generado por <strong style={{ color: '#4F46E5' }}>Kapri.me</strong> — Plataforma de aprendizaje activo con IA</span>
                    <span>{new Date().toLocaleDateString('es-MX', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                  </div>
                </div>

                {/* Bottom CTA */}
                <div
                  className="mt-8 rounded-xl p-5 no-print"
                  style={{ background: 'rgba(251,191,36,0.06)', border: '1px solid rgba(251,191,36,0.15)' }}
                >
                  <div className="flex items-center gap-2 mb-3">
                    <Zap className="w-4 h-4" style={{ color: '#FBBF24' }} />
                    <span className="text-sm font-semibold" style={{ color: '#FBBF24' }}>¿Listo para practicar?</span>
                  </div>
                  <div className="flex gap-3">
                    <button
                      onClick={() => { loadQuiz() }}
                      className="btn-primary flex items-center gap-2 text-sm"
                    >
                      Iniciar Quiz <ChevronRight className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => { loadMindmap() }}
                      className="text-sm px-4 py-2 rounded-xl font-semibold transition-all"
                      style={{ color: '#818CF8', border: '1px solid rgba(79,70,229,0.3)', background: 'transparent' }}
                      onMouseEnter={e => { e.currentTarget.style.background = 'rgba(79,70,229,0.1)' }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'transparent' }}
                    >
                      Ver Mapa Mental
                    </button>
                  </div>
                </div>
              </div>

              {/* Sidebar: key concepts */}
              {keyConcepts.length > 0 && (
                <div className="no-print">
                  <div className="glass-card p-5 sticky top-20">
                    <div className="flex items-center gap-2 mb-4">
                      <Zap className="w-4 h-4" style={{ color: '#FBBF24' }} />
                      <span className="text-xs font-bold uppercase tracking-widest" style={{ color: '#64748B' }}>Conceptos clave</span>
                    </div>
                    <div className="space-y-2">
                      {keyConcepts.map((concept, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <div
                            className="w-2 h-2 rounded-full flex-shrink-0"
                            style={{ background: CONCEPT_COLORS[i % CONCEPT_COLORS.length] }}
                          />
                          <span className="text-sm leading-tight" style={{ color: '#94A3B8', fontSize: 14 }}>
                            {concept}
                          </span>
                        </div>
                      ))}
                    </div>
                    <div className="mt-5 pt-4" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                      <p className="text-xs" style={{ color: '#334155' }}>Generado con IA · Kapri</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )
      })()}

      {/* ======= QUIZ MODE ======= */}
      {mode === 'quiz' && quiz && !isGenerating && (
        <div className="space-y-4">
          {/* Warning banner when last regeneration failed */}
          {quiz.regenerationFailed && (
            <div className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm"
                 style={{ background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.3)', color: '#FCD34D' }}>
              <span>⚠</span>
              <span>La última regeneración falló. Mostrando el quiz anterior.</span>
              <button
                className="ml-auto text-xs underline opacity-70 hover:opacity-100"
                onClick={() => setQuiz((q) => q ? { ...q, regenerationFailed: false } : q)}
              >Descartar</button>
            </div>
          )}
          {/* Quiz header bar: material + difficulty + regenerate + progress */}
          {!showFinalResults && (
            <div className="glass-card px-5 py-4 space-y-3">
              {/* Row 1: material name */}
              <div className="flex items-center justify-between">
                <span
                  className="text-xs font-bold uppercase tracking-widest truncate flex-1"
                  style={{ color: '#64748B' }}
                >
                  {material?.title ?? 'Quiz'}
                </span>
                <button
                  onClick={handleRegenerateQuiz}
                  disabled={isGenerating}
                  title="Regenerar quiz con opciones actuales"
                  className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg transition-all disabled:opacity-50 flex-shrink-0"
                  style={{ color: '#818CF8', background: 'rgba(79,70,229,0.08)', border: '1px solid rgba(79,70,229,0.2)' }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(79,70,229,0.18)' }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(79,70,229,0.08)' }}
                >
                  <RefreshCw className="w-3 h-3" /> Regenerar
                </button>
              </div>

              {/* Row 2: difficulty + question count */}
              <div className="flex items-center gap-4 flex-wrap">
                <div className="flex items-center gap-1">
                  <span className="text-xs mr-1.5" style={{ color: '#475569' }}>Dificultad:</span>
                  {DIFFICULTY_OPTIONS.map((opt) => {
                    const active = difficulty === opt.value
                    const disabled = opt.value !== 'medium' && !isPro
                    return (
                      <button
                        key={opt.value}
                        onClick={() => disabled ? openUpgradeModal('difficulty_levels') : setDifficulty(opt.value)}
                        className="px-2.5 py-1 rounded-lg text-xs font-semibold transition-all"
                        style={{
                          background: active ? `${opt.color}20` : 'rgba(255,255,255,0.04)',
                          border: `1px solid ${active ? opt.color + '50' : 'rgba(255,255,255,0.06)'}`,
                          color: disabled ? '#334155' : active ? opt.color : '#64748B',
                        }}
                      >
                        {opt.label}
                      </button>
                    )
                  })}
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-xs mr-1.5" style={{ color: '#475569' }}>Preguntas:</span>
                  {[10, 15, 20, 25].map((n) => {
                    const active = questionCount === n
                    return (
                      <button
                        key={n}
                        onClick={() => setQuestionCount(n)}
                        className="w-8 h-7 rounded-lg text-xs font-semibold transition-all"
                        style={{
                          background: active ? 'rgba(79,70,229,0.2)' : 'rgba(255,255,255,0.04)',
                          border: `1px solid ${active ? '#4F46E5' : 'rgba(255,255,255,0.06)'}`,
                          color: active ? '#818CF8' : '#64748B',
                        }}
                      >
                        {n}
                      </button>
                    )
                  })}
                </div>
              </div>
              {/* Row 3: version history (only when there are multiple versions) */}
              {quizVersions.length > 1 && (
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs flex-shrink-0" style={{ color: '#475569' }}>Versiones:</span>
                  {quizVersions.map((v, idx) => {
                    const isActive = quiz.versionId === v.version_id || (quiz.versionId === undefined && idx === 0)
                    const diffColor = v.difficulty === 'hard' ? '#EF4444' : v.difficulty === 'easy' ? '#10B981' : '#FBBF24'
                    const label = idx === 0 ? 'Actual' : `v${quizVersions.length - idx}`
                    const diffLabel = v.difficulty === 'hard' ? 'Dif' : v.difficulty === 'easy' ? 'Fác' : 'Med'
                    return (
                      <button
                        key={v.version_id}
                        onClick={() => handleLoadVersion(v)}
                        disabled={isLoadingVersion}
                        title={`${new Date(v.created_at).toLocaleString('es', { dateStyle: 'short', timeStyle: 'short' })} · ${v.question_count} preguntas · ${v.difficulty}`}
                        className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium transition-all disabled:opacity-50"
                        style={{
                          background: isActive ? 'rgba(79,70,229,0.18)' : 'rgba(255,255,255,0.04)',
                          border: `1px solid ${isActive ? '#4F46E5' : 'rgba(255,255,255,0.08)'}`,
                          color: isActive ? '#818CF8' : '#64748B',
                        }}
                      >
                        <span>{label}</span>
                        <span className="text-xs px-1 rounded" style={{ background: `${diffColor}20`, color: diffColor }}>{diffLabel}</span>
                        <span style={{ color: '#475569' }}>{v.question_count}p</span>
                      </button>
                    )
                  })}
                  {isLoadingVersion && <span className="text-xs" style={{ color: '#64748B' }}>Cargando...</span>}
                </div>
              )}

              {/* Row 4: progress */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs font-semibold" style={{ color: '#94A3B8' }}>
                    Pregunta {currentQuestion + 1} de {quiz.questions.length}
                  </span>
                  <span className="text-xs font-semibold" style={{ color: '#64748B' }}>
                    {Math.round(((currentQuestion + 1) / quiz.questions.length) * 100)}% completado
                  </span>
                </div>
                <div className="w-full h-1 rounded-full" style={{ background: 'rgba(255,255,255,0.06)' }}>
                  <div
                    className="h-1 rounded-full transition-all"
                    style={{
                      width: `${((currentQuestion + 1) / quiz.questions.length) * 100}%`,
                      background: 'linear-gradient(90deg, #4F46E5, #8B5CF6)',
                    }}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Final results */}
          {showFinalResults && (
            <div
              className="glass-card p-8 text-center"
              style={{ border: '1px solid rgba(79,70,229,0.3)' }}
            >
              <Trophy className="w-12 h-12 mx-auto mb-4" style={{ color: '#FBBF24' }} />
              <p className="text-5xl font-bold mb-2" style={{
                color: score >= 80 ? '#10B981' : score >= 50 ? '#FBBF24' : '#EF4444',
              }}>
                {score}%
              </p>
              <p className="text-sm mb-1" style={{ color: '#94A3B8' }}>
                {quiz.questions.filter((q) => answers[q.id] === q.correctAnswer).length} de{' '}
                {quiz.questions.length} correctas
              </p>
              <p className="text-xs mb-6" style={{ color: '#475569' }}>
                {score >= 80 ? '¡Excelente trabajo!' : score >= 50 ? 'Buen esfuerzo, sigue practicando.' : 'Repasa el material y vuelve a intentarlo.'}
              </p>
              <div className="flex gap-3 justify-center">
                <button onClick={resetQuiz} className="btn-secondary flex items-center gap-2">
                  <RotateCcw className="w-4 h-4" /> Repetir quiz
                </button>
                <button onClick={() => navigate('/results')} className="btn-primary">
                  Ver historial
                </button>
              </div>
            </div>
          )}

          {/* One-by-one question */}
          {!showFinalResults && q && (
            <div className="space-y-4">
              <QuizCard
                question={q}
                selectedAnswer={answers[q.id]}
                onAnswerSelect={handleAnswerSelect}
                immediateReveal={true}
                locked={answeredCurrent}
              />

              <div className="flex gap-3">
                {currentQuestion > 0 && (
                  <button
                    onClick={handlePrevQuestion}
                    className="btn-secondary flex items-center justify-center gap-2 px-4"
                  >
                    <ChevronLeft className="w-4 h-4" /> Anterior
                  </button>
                )}
                {(answeredCurrent || !!answers[q.id]) && (
                  <button
                    onClick={handleNextQuestion}
                    className="btn-primary flex-1 flex items-center justify-center gap-2"
                  >
                    {currentQuestion < quiz.questions.length - 1 ? (
                      <>Siguiente pregunta <ChevronRight className="w-4 h-4" /></>
                    ) : (
                      <>Ver resultados <Trophy className="w-4 h-4" /></>
                    )}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ======= MINDMAP MODE ======= */}
      {mode === 'mindmap' && mindmap && !isGenerating && (
        <MindmapViewer mindmap={mindmap} material={material} onBack={() => setMode('select')} />
      )}

      {/* ======= CHAT MODE (Sprint 1-C) ======= */}
      {mode === 'chat' && (
        <ChatPanel
          messages={chatMessages}
          streamingMessageId={chatStreamingId}
          streamingText={chatStreamingText}
          isSending={chatSending}
          onSend={sendChat}
          onClear={clearChat}
          error={chatError}
        />
      )}
    </div>
  )
}
